/**
 * The timesheet gate (prototype VIEWS.catchup): an ops member whose previous working day was not filed
 * cannot open anything else until they fill it in. The shell renders this in place of every page while
 * GET /me → gate.timesheet_blocked is true (the server decides; the AD and founder are never gated).
 *
 * Items come from GET /me/timesheet/gate (that day's plan); POST /me/timesheet/catch-up files the day as
 * `late` and lifts the gate. If the missed day was an optional holiday the person may still take, they can
 * take it instead (POST /me/optional-holidays) — expected hours become 0 and the gate lifts.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { CatchUpBody } from '@amplispot/contracts';
import { ApiFailure, call, newIdempotencyKey } from '../../api';
import { useSession } from '../../lib/session';
import { addDays } from '../../lib/work';
import { todayIso } from '../../lib/format';
import { shortDay, useGate, useMyOptional, useTsWrite } from '../../lib/timesheets';
import { ErrorState, Loading, errorText, useToast } from '../../ui/kit';
import { ClientLogo } from '../clients/parts';

type St = 'done' | 'part' | 'no';
const SEG: [St, string][] = [
  ['done', 'Done'],
  ['part', 'Partly'],
  ['no', 'Not done'],
];

export function CatchUp() {
  const { me } = useSession();
  const gate = useGate();
  const nav = useNavigate();
  const toast = useToast();
  const prev = gate.data?.previous_working_day ?? me.gate?.previous_working_day ?? null;
  const opt = useMyOptional(Number((prev ?? todayIso()).slice(0, 4)), !!prev);
  const [st, setSt] = useState<Record<string, St>>({});
  const [act, setAct] = useState<Record<string, number>>({});
  const [extras, setExtras] = useState<{ text: string; minutes: number }[]>([]);
  const [exT, setExT] = useState('');
  const [exM, setExM] = useState('');
  const [refl, setRefl] = useState('');

  const submit = useTsWrite((b: CatchUpBody) => call('me.timesheetCatchUp', { body: b, idempotencyKey: newIdempotencyKey() }));
  const elect = useTsWrite((dayId: string) => call('me.optionalHolidaysElect', { body: { holiday_day_id: dayId }, idempotencyKey: newIdempotencyKey() }));

  if (gate.isLoading) return <Loading label="Checking your timesheet…" />;
  if (gate.error) return <ErrorState error={gate.error} onRetry={() => void gate.refetch()} />;
  const items = gate.data?.items_to_answer ?? [];
  if (!gate.data?.blocked || !prev) {
    // The gate lifted (filed elsewhere, a manager filed it, or a holiday was published) — back to the day.
    return (
      <div className="panel">
        <div className="empty">
          <b>Your day is open</b>
          <button className="btn pri" style={{ marginTop: 12 }} onClick={() => nav(me.home_route)}>
            Go to Today
          </button>
        </div>
      </div>
    );
  }
  const state = (id: string) => st[id] ?? items.find((i) => i.task_id === id)?.state ?? undefined;
  const optDay = opt.data?.available.find((a) => a.day.the_date === prev && !a.elected && a.editable);

  const add = () => {
    const t = exT.trim();
    if (t.length < 2) return toast('Say what you did');
    setExtras((x) => [...x, { text: t, minutes: Math.max(1, Number(exM) || 15) }]);
    setExT('');
    setExM('');
  };
  const send = () => {
    if (items.some((i) => !state(i.task_id))) return toast('Say how each task went first');
    if (!items.length && !extras.length) return toast('Add what you worked on that day');
    submit.mutate(
      {
        work_date: prev,
        answers: items.map((i) => {
          const s = state(i.task_id)!;
          return { task_id: i.task_id, state: s, actual_minutes: s === 'no' ? 0 : Math.round(act[i.task_id] ?? i.planned_minutes) };
        }),
        extras: extras.map((e) => ({ text: e.text, minutes: e.minutes })),
        ...(refl.trim() ? { reflection: refl.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast('Thank you. Your day is open.');
          nav(me.home_route);
        },
        onError: (e) => toast(e instanceof ApiFailure && e.fieldErrors['body'] ? e.fieldErrors['body'] : errorText(e)),
      },
    );
  };

  return (
    <>
      <div className="ts-hero" style={{ background: 'linear-gradient(135deg,#3A3866,#1C1B3A)', boxShadow: '0 16px 34px rgba(28,27,58,.35)' }}>
        <div className="grow">
          <h2>Fill {shortDay(prev)}’s timesheet to open today</h2>
          <p>
            It was not filed {prev === addDays(todayIso(), -1) ? 'yesterday' : 'on the day'}. Today’s plan opens as soon as you submit this. It takes two minutes.
          </p>
        </div>
        <span className="chip" style={{ background: 'rgba(255,255,255,.18)', color: '#fff' }}>
          Locked until filed
        </span>
      </div>
      {optDay && (opt.data?.remaining ?? 0) > 0 ? (
        <div className="note" style={{ marginBottom: 16 }}>
          <b>{shortDay(prev)} was {optDay.day.name}, an optional holiday.</b> If you took it off, take it as one of your optional holidays ({opt.data!.remaining} of {opt.data!.allowance} left this year) and there is nothing to fill in.
          <div style={{ marginTop: 10 }}>
            <button
              className="btn sm"
              disabled={elect.isPending}
              onClick={() =>
                elect.mutate(optDay.day.id, {
                  onSuccess: () => {
                    toast(`${optDay.day.name} taken as an optional holiday. Your day is open.`);
                    nav(me.home_route);
                  },
                  onError: (e) => toast(errorText(e)),
                })
              }
            >
              I took {optDay.day.name} off
            </button>
          </div>
        </div>
      ) : null}
      <div className="panel">
        <table>
          <thead>
            <tr>
              <th>What you worked on</th>
              <th>Planned</th>
              <th>Actual minutes</th>
              <th>How did it go</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const s = state(i.task_id);
              return (
                <tr key={i.task_id}>
                  <td>
                    <div className="split">
                      {i.client ? <ClientLogo id={i.client.id} name={i.client.name} size={30} /> : null}
                      <div>
                        <b>{i.title}</b>
                        <div className="sub">{i.client ? i.client.name : 'Internal'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="num">{i.planned_minutes} min</td>
                  <td>
                    <input
                      className="in mins"
                      type="number"
                      min={0}
                      step={5}
                      value={act[i.task_id] ?? i.planned_minutes}
                      onChange={(e) => setAct((x) => ({ ...x, [i.task_id]: Math.max(0, Number(e.target.value) || 0) }))}
                      aria-label={`Actual minutes for ${i.title}`}
                    />
                  </td>
                  <td>
                    <div className="seg">
                      {SEG.map(([k, l]) => (
                        <button
                          key={k}
                          className={s === k ? 'on' : ''}
                          style={s === k && k === 'no' ? { background: 'var(--cold)' } : s === k && k === 'part' ? { background: 'var(--warm)' } : undefined}
                          onClick={() => setSt((x) => ({ ...x, [i.task_id]: k }))}
                          aria-pressed={s === k}
                        >
                          {l}
                        </button>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!items.length ? (
              <tr>
                <td colSpan={4} className="sub">
                  Nothing was planned for that day. Add what you worked on below.
                </td>
              </tr>
            ) : null}
            {extras.map((x, k) => (
              <tr key={k}>
                <td colSpan={2}>
                  <b>{x.text}</b>
                  <div className="sub">Not in the plan</div>
                </td>
                <td className="num">{x.minutes} min</td>
                <td>
                  <button className="btn sm ghost" onClick={() => setExtras((e) => e.filter((_, j) => j !== k))}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            <tr>
              <td colSpan={4}>
                <div className="split">
                  <input className="in" placeholder="Anything else you did that day" value={exT} onChange={(e) => setExT(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
                  <input className="in mins" type="number" placeholder="Min" value={exM} onChange={(e) => setExM(e.target.value)} aria-label="Minutes" />
                  <button className="btn sm" onClick={add}>
                    Add
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <div className="bd" style={{ borderTop: '1px solid var(--line)' }}>
          <label className="fld">
            <span>A line on how the day went</span>
            <textarea className="in" placeholder="Optional" value={refl} onChange={(e) => setRefl(e.target.value)} />
          </label>
          <div className="split">
            <div className="grow" />
            <button className="btn pri" onClick={send} disabled={submit.isPending}>
              Submit and open today
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
